import { useState, useCallback } from 'react';
import { GeneratedPost, PreviewFormat } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';

export interface QuickGenConfig {
  // Origen del brand: o una URL a scrapear, o un workspace ya existente.
  url?: string;
  workspaceId?: string;
  workspaceName?: string;
  format: PreviewFormat;
  model: 'ai' | 'stock' | 'asset';
  count?: number;        // default 4, cap 10
  language?: string;
  kieModel?: string;
  /** Estilos elegidos para "baked AI" posters (full IA con texto baked dentro,
   *  sin template). Si hay >=1, el 100% de los ads se generan baked-ai haciendo
   *  round-robin entre estos estilos. Si esta vacio, va por el `model` normal. */
  styleIds?: string[];
  /** Si true, integra el logo real del brand en el poster generado. */
  useLogo?: boolean;
  /**
   * Los dos prompts que escribe la persona, y son DOS a propósito: hasta hoy
   * había un solo campo de texto libre y el cliente metía ahí el brief de la
   * imagen, que alimentaba el copy. Separarlos es el arreglo.
   */
  imagePrompt?: string;      // "Describe your image" → prompt de la IMAGEN
  imagePromptExact?: boolean; // el prompt de imagen va tal cual, sin marca
  copyPrompt?: string;       // "What should the copy say" → texto del post
}

interface PrepareResponse {
  workspaceId: string;
  workspaceName: string;
  isNew: boolean;
}

type Phase = 'idle' | 'preparing' | 'generating' | 'done' | 'error';

const MODEL_TO_SOURCE_CONFIG: Record<QuickGenConfig['model'], {
  enabledSources: string[];
  mix: Record<string, number>;
}> = {
  ai:    { enabledSources: ['ai'],    mix: { stock: 0,   pastel: 0,   asset: 0,   ai: 100 } },
  stock: { enabledSources: ['stock'], mix: { stock: 100, pastel: 0,   asset: 0,   ai: 0 } },
  asset: { enabledSources: ['asset'], mix: { stock: 0,   pastel: 0,   asset: 100, ai: 0 } },
};

/**
 * Quick Gen: orquesta el flujo "URL -> ads".
 * Etapas:
 *   1) preparing  -> POST /api/v2/quickgen/prepare (resuelve workspace + scrape)
 *   2) generating -> stream a /api/workspaces/:id/generate-stream
 *   3) done       -> el caller navega a /quick-gen/preview con los posts
 *
 * El stream se reutiliza del flow normal para no duplicar logica de generacion.
 * Los endpoints `/api/v2/*` mantienen su nombre interno aunque la UI ya no
 * exponga el concepto de "v2".
 */
export function useQuickGenerate() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [phaseMessage, setPhaseMessage] = useState('');
  const [streamingPosts, setStreamingPosts] = useState<GeneratedPost[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const reset = useCallback(() => {
    setPhase('idle');
    setPhaseMessage('');
    setStreamingPosts([]);
    setProgress({ current: 0, total: 0 });
    setWorkspaceId(null);
    setError('');
  }, []);

  const run = useCallback(async (config: QuickGenConfig) => {
    reset();
    setPhase('preparing');
    setPhaseMessage('Preparing your brand...');

    let resolved: PrepareResponse;
    if (config.workspaceId) {
      // Workspace existente: usamos su brand tal cual, sin scrapear ninguna URL.
      resolved = { workspaceId: config.workspaceId, workspaceName: config.workspaceName || 'your workspace', isNew: false };
      setWorkspaceId(resolved.workspaceId);
    } else {
      try {
        resolved = await apiClient.post<PrepareResponse>('/api/v2/quickgen/prepare', {
          url: config.url,
        });
        setWorkspaceId(resolved.workspaceId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not prepare the workspace';
        setError(msg);
        setPhase('error');
        return null;
      }
    }

    setPhase('generating');
    setPhaseMessage(resolved.isNew
      ? `Generating ads for ${resolved.workspaceName}...`
      : `Generating ads (existing workspace: ${resolved.workspaceName})...`);

    const count = Math.min(10, Math.max(1, config.count ?? 4));
    const baseCfg = MODEL_TO_SOURCE_CONFIG[config.model];

    // Si hay estilos elegidos: 100% baked-ai (el server hace round-robin entre
    // `styleIds`). Si no, va por el `model` normal (ai/stock/asset).
    const styleIds = config.styleIds ?? [];
    const sourceCfg = styleIds.length > 0
      ? {
          enabledSources: ['baked-ai'],
          mix: { stock: 0, pastel: 0, asset: 0, ai: 0, 'baked-ai': 100 },
        }
      : baseCfg;

    const generatedPosts: GeneratedPost[] = [];

    try {
      await apiClient.stream(
        `/api/workspaces/${resolved.workspaceId}/generate-stream`,
        {
          platformCounts: { instagram: count },
          customPrompt: (config.copyPrompt || '').trim(),
          imagePrompt: (config.imagePrompt || '').trim(),
          imagePromptExact: !!config.imagePromptExact,
          language: config.language,
          previewFormat: config.format,
          imageSourceConfig: {
            enabledSources: sourceCfg.enabledSources,
            mix: sourceCfg.mix,
            selectedAssetIds: [],
            customColors: [],
            kieModel: config.kieModel,
            bakedAdStyleIds: styleIds,
            bakedAdUseLogo: config.useLogo ?? false,
          },
        },
        (event, data) => {
          switch (event) {
            case 'init':
              setProgress({ current: 0, total: (data as { totalPosts: number }).totalPosts });
              break;
            case 'post': {
              const d = data as { post: GeneratedPost; index: number; total: number };
              generatedPosts.push(d.post);
              setStreamingPosts([...generatedPosts]);
              setProgress({ current: d.index + 1, total: d.total });
              break;
            }
            case 'complete':
              setPhase('done');
              break;
            case 'error':
              setError((data as { message?: string }).message || 'Could not generate the ads');
              setPhase('error');
              break;
          }
        },
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'The connection dropped while generating';
      setError(msg);
      setPhase('error');
      return null;
    }

    return { workspaceId: resolved.workspaceId, posts: generatedPosts };
  }, [reset]);

  return {
    phase, phaseMessage, streamingPosts, progress, workspaceId, error,
    run, reset,
  };
}
